/**
 * Wave 3 — Admin investor payment ops (grant / deny pending checkouts).
 */

import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'sonner';

import {
  useDenyAdminSubscriptionMutation,
  useGetAdminSubscriptionsQuery,
  useGrantAdminSubscriptionMutation,
} from '@/apis/extended/subscriptionApi';
import { TastePageHeader } from '@/components/Common/TastePageHeader';
import { TasteSectionCard } from '@/components/Common/TasteSectionCard';

export const AdminPaymentsPage = () => {
  const [statusFilter, setStatusFilter] = useState('pending');
  const {
    data = [],
    isLoading,
    refetch,
  } = useGetAdminSubscriptionsQuery(
    statusFilter ? { status: statusFilter } : undefined
  );
  const [grant, { isLoading: granting }] = useGrantAdminSubscriptionMutation();
  const [deny, { isLoading: denying }] = useDenyAdminSubscriptionMutation();

  const rows = useMemo(() => data, [data]);

  const handleGrant = async (id: string) => {
    try {
      await grant({ id }).unwrap();
      toast.success('Access granted — user emailed');
      refetch();
    } catch (e: any) {
      toast.error(e?.data?.message || 'Grant failed');
    }
  };

  const handleDeny = async (id: string) => {
    const note = window.prompt('Optional note for the user:') || undefined;
    try {
      await deny({ id, note }).unwrap();
      toast.success('Request denied — user emailed');
      refetch();
    } catch (e: any) {
      toast.error(e?.data?.message || 'Deny failed');
    }
  };

  return (
    <Box>
      <TastePageHeader
        title="Investor payments"
        subtitle="Durable pending → grant/deny. Stripe can plug into the same checkout seam later."
        actions={
          <TextField
            select
            size="small"
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="denied">Denied</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
            <MenuItem value="">All</MenuItem>
          </TextField>
        }
      />

      <TasteSectionCard>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Plan</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Provider</TableCell>
              <TableCell>Requested</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={6}>Loading…</TableCell>
              </TableRow>
            )}
            {!isLoading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  No subscriptions for this filter.
                </TableCell>
              </TableRow>
            )}
            {rows.map((row) => (
              <TableRow key={row._id}>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    {row.user?.name || '—'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {row.user?.email || row.userId}
                  </Typography>
                </TableCell>
                <TableCell>
                  {row.plan} · ${row.amount}/{row.billingCycle}
                </TableCell>
                <TableCell>
                  <Chip size="small" label={row.status} />
                </TableCell>
                <TableCell>{row.provider || row.paymentMethod}</TableCell>
                <TableCell>
                  {row.requestedAt || row.createdAt
                    ? new Date(
                        row.requestedAt || row.createdAt
                      ).toLocaleString()
                    : '—'}
                </TableCell>
                <TableCell align="right">
                  {row.status === 'pending' && (
                    <Stack
                      direction="row"
                      spacing={1}
                      justifyContent="flex-end"
                    >
                      <Button
                        size="small"
                        variant="contained"
                        disabled={granting}
                        onClick={() => handleGrant(row._id)}
                      >
                        Grant
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        disabled={denying}
                        onClick={() => handleDeny(row._id)}
                      >
                        Deny
                      </Button>
                    </Stack>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TasteSectionCard>
    </Box>
  );
};
